import { alpha, RadioGroup, Stack, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { Hourglass, InfoIcon } from "lucide-react";
import {
  useEffect,
  useState } from "react";
import { useTranslation } from "react-i18next";
import type { IEditions } from "types";
import { EditionsContainer } from "./Editions.styles";
import { oneEditionIsEnrolled } from "./utils";
import { ButtonDigited } from "../../../components/Button/Button.variants";
import { TitleAndSubtitle } from "../../../components/Commons/TitleAndSubtitle.component";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner.component";
import { EditionCard } from "../../../components/ModalEditionChange/EditionCard.component";
import { SkeletonCard } from "../../ModalEditionChange/SkeletonCard";

export const Editions = ({
  title,
  description,
  learningObject,
  isVirtual,
  isLoading,
  noPadding=true,
  editionsQueryData
}: IEditions) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(null);
  const [expandedEditionId, setExpandedEditionId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  const { selfEnrollment } = learningObject;

  const {
    editions,
    handleEditionChange,
    isPollingEditionChange
  } = editionsQueryData ?? {};

  const enrolled = oneEditionIsEnrolled(editions);
  dayjs.locale(i18n.language);

  const handleEnroll = () => {
    if (selectedEditionId !== null && handleEditionChange) {
      handleEditionChange(selectedEditionId);
    }
  };

  const toggleAccordion = (editionId: number) => {
    setExpandedEditionId(expandedEditionId === editionId ? null : editionId);
  };

  const editionsLoaded = editions !== undefined;
  const noEditionsAvailable = () => editionsLoaded && editions.length === 0;
  const showWaitingEnrollment = editionsLoaded && !noEditionsAvailable() && !selfEnrollment && !enrolled;

  // close edit mode when editions change
  useEffect(() => {
    setEditMode(false);
  }, [editions]);

  return (
    <>
      <TitleAndSubtitle
        title={ title }
        subTitle={ selfEnrollment ? description : "" }
        sxSubTitle={ {
          marginBottom:"0.75rem"
        } }
      />
      <EditionsContainer
        id="editions-tab-container"
        data-testid="editions-tab-container"
        aria-label={ t("editions_tab_aria_label") }
        noPadding={ noPadding }
      >
        { !editionsLoaded
          ? null
          : noEditionsAvailable()
            ? (
              <div style={ { marginBottom: "1rem" } } data-testid="editions-empty-state">
                <InfoBanner
                  id="editions-empty"
                  ariaLabel={ t("editions_empty_banner_aria_label") }
                  backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                  textColor={ theme.customColors.textTag }
                  borderColor={ theme.customColors.backgroundAccent }
                  icon={ <InfoIcon size={ "1.5rem" } color={ theme.customColors.textTag } /> }
                  title={ t("info_banner_title_editions") }
                  label={ t("info_banner_description_editions") }
                />
              </div>
            )
            : showWaitingEnrollment
              ? (
                <Stack
                  data-testid="editions-waiting-enrollment"
                  sx={ { marginBottom: "1rem" } }
                >
                  <InfoBanner
                    id="editions-waiting-enrollment"
                    ariaLabel={ t("editions_waiting_enrollment_aria_label") }
                    backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                    textColor={ theme.customColors.textTag }
                    borderColor={ theme.customColors.backgroundAccent }
                    icon={ <Hourglass size={ "1.25rem" } color={ theme.customColors.textTag } /> }
                    label={ t("waiting_enrollment", "A breve verrai iscritto a un'edizione dell'aula virtuale") }
                    sxContainer={ {
                      padding: "0.5rem 1rem"
                    } }
                  />
                </Stack>
              )
              : isPollingEditionChange ? <SkeletonCard />
                : (
                  <>
                    <RadioGroup
                      id="editions-radio-group"
                      data-testid="editions-radio-group"
                      aria-label={ t("editions_radio_group_aria_label") }
                      value={ selectedEditionId }
                      onChange={ (e) => setSelectedEditionId(Number(e.target.value)) }
                      sx={ {
                        maxHeight: "30rem",
                        overflowY: "auto",
                        flexDirection: "column",
                        flexWrap: "nowrap",
                        "::-webkit-scrollbar": {
                          width: "0.44rem",
                          backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}`,
                          borderRadius: "0.5rem"
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}!important`,
                          borderRadius: "0.5rem"
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: `${alpha(theme.customColors.exploreCardText, 1)}!important`,
                          borderRadius: "0.5rem"
                        }
                      } }
                    >
                      { editions?.
                        sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                        .map((edition, idx) => (
                          <EditionCard
                            key={ edition.id + idx }
                            learningObject={ learningObject }
                            edition={ edition }
                            editMode={ editMode }
                            isVirtual={ !!isVirtual }
                            isExpanded={ expandedEditionId === edition.id }
                            onToggleAccordion={ () => toggleAccordion(edition.id) }
                            oneEditionIsEnrolled={ enrolled }
                            theme={ theme }
                            t={ t }
                          />
                        )) }

                    </RadioGroup>
                    { /* utente enrolled, puo' iscriversi e non e' in edit mode */ }
                    { enrolled && selfEnrollment && !editMode && (
                      <ButtonDigited
                        id="editions-modify-button"
                        data-testid="editions-modify-button"
                        aria-label={ t("editions_modify_button_aria_label") }
                        typology="secondary"
                        onClick={ () => setEditMode(true) }
                      >
                        { t("modify") }
                      </ButtonDigited>
                    ) }

                    { /* utente enrolled, puo' iscriversi ed e' in edit mode */ }
                    { enrolled && selfEnrollment && editMode && (
                      <ButtonDigited
                        id="editions-save-button"
                        data-testid="editions-save-button"
                        aria-label={ t("editions_save_button_aria_label") }
                        typology="secondary"
                        onClick={ handleEnroll }
                      >
                        { t("save") }
                      </ButtonDigited>
                    ) }

                    { /* utente non enrolled, puo' iscriversi */ }
                    { !enrolled && selfEnrollment && !editMode && (
                      <ButtonDigited
                        id="editions-enroll-button"
                        data-testid="editions-enroll-button"
                        aria-label={ t("editions_enroll_button_aria_label") }
                        typology="primary"
                        disabled={ selectedEditionId === null || isLoading || isPollingEditionChange }
                        onClick={ handleEnroll }
                      >
                        { t("enroll_edition") }
                      </ButtonDigited>
                    ) }
                  </>
                )
        }
      </EditionsContainer>
    </>
  );
};
